import React, { useState, useEffect, useMemo } from 'react';
import { Typography, TextField, Grid2, Card, CardContent, MenuItem, Box, InputAdornment, useTheme, Tooltip } from '@mui/material';
import { Search, Person, Email, Phone, Home, Group, Category, Notes } from '@mui/icons-material';
import { getEventsGuests } from '../actions/guests';
import { useLocation } from 'react-router-dom';
import { cardGridSx, cardItemSx } from '../utils/cardLayout';
import GuestInvitationDialog from '../components/GuestInvitationDialog';
import GuestInvitationButton from '../components/GuestInvitationButton';

const MultiViewGuestListManagement = () => {
  const theme = useTheme();
  const location = useLocation();
  
  const eventIds = useMemo(() => {
    const queryParam = new URLSearchParams(location.search).get('eventIds');
    return queryParam ? queryParam.split(',') : [];
  }, [location.search]);

  const [guests, setGuests] = useState([]);
  const [invitationGuest, setInvitationGuest] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterRSVP, setFilterRSVP] = useState('');

  // Fetch guests from API on load
  useEffect(() => {
    let active = true;
    const fetchGuests = async () => {
      try {
        const fetchedGuests = await getEventsGuests(eventIds);
        if (active) setGuests(fetchedGuests);
      } catch (error) {
        if (active) console.error('Failed to fetch guests', error);
      }
    };
    fetchGuests();
    window.addEventListener('focus', fetchGuests);
    return () => { active = false; window.removeEventListener('focus', fetchGuests); };
  }, [eventIds]);

  // Filter guests based on search query, category, and RSVP status
  const filteredGuests = guests.filter((guest) => {
    return (
      guest.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      (filterCategory === '' || guest.category === filterCategory) &&
      (filterRSVP === '' || guest.rsvp === filterRSVP)
    );
  });

  const getRSVPColor = (rsvp) => {
    const colorMap = {
      'Attending': theme.palette.success.main,
      'Not Attending': theme.palette.error.main,
      'Pending': theme.palette.warning.main,
    };
    return colorMap[rsvp] || theme.palette.text.secondary;
  };

  const getCardBorderColor = (rsvp) => {
    const borderColorMap = {
      'Attending': '2px solid ' + theme.palette.success.main,
      'Not Attending': '2px solid ' + theme.palette.error.main,
      'Pending': '2px solid ' + theme.palette.warning.main,
    };
    return borderColorMap[rsvp] || (theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid rgba(255, 255, 255, 0.2)');
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <GuestInvitationDialog guest={invitationGuest} onClose={() => setInvitationGuest(null)} onChanged={invitation => setGuests(items => items.map(guest =>
        guest.guest_event_id === invitationGuest?.guest_event_id ? { ...guest, invitation: invitation === null ? null : { ...guest.invitation, ...invitation } } : guest))} />
      {/* Filter Section */}
      <Box sx={{ mb: 4, mt: { xs: 4, sm: 0 } }}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 12, md: 2 }}>
            <TextField
              label="Search Guest by Name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }
              }}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 2 }}>
            <TextField
              select
              label="Filter by Category"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            >
              <MenuItem value="">All Categories</MenuItem>
              <MenuItem value="Family">Family</MenuItem>
              <MenuItem value="Friends">Friends</MenuItem>
              <MenuItem value="VIP">VIP</MenuItem>
            </TextField>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 2 }}>
            <TextField
              select
              label="Filter by RSVP"
              value={filterRSVP}
              onChange={(e) => setFilterRSVP(e.target.value)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            >
              <MenuItem value="">All RSVP</MenuItem>
              <MenuItem value="Attending">Attending</MenuItem>
              <MenuItem value="Not Attending">Not Attending</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
            </TextField>
          </Grid2>
        </Grid2>
      </Box>

      {/* Guest List */}
      <Box sx={cardGridSx()}>
        {filteredGuests.length === 0 ? (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Card sx={{ 
              p: { xs: 3, sm: 4 }, 
              textAlign: 'center',
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                : '0 4px 20px rgba(0, 0, 0, 0.1)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(255, 255, 255, 0.2)',
            }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                {(searchQuery || filterCategory || filterRSVP) ? 
                  'No guests found for the selected filters' : 
                  'No guests found for the selected events'}
              </Typography>
            </Card>
          </Box>
        ) : filteredGuests.map((guest, index) => (
          <Box key={guest.guest_event_id ?? index} sx={cardItemSx()}>
            <Card sx={{
              height: '100%',
              borderRadius: 3,
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                : '0 4px 20px rgba(0, 0, 0, 0.1)',
              border: getCardBorderColor(guest.rsvp),
              transition: 'all 0.3s ease',
              position: 'relative',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 8px 30px rgba(0, 0, 0, 0.3)'
                  : '0 8px 30px rgba(0, 0, 0, 0.15)',
              }
            }}>
              {/* Plus One Badge - Top Right */}
              {guest.plus_one > 0 && (
                <Box sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  zIndex: 1,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}>
                  +{guest.plus_one}
                </Box>
              )}

              <CardContent sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Tooltip title={guest.name} arrow placement="top">
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600, 
                    mb: 2, 
                    minHeight: '1.5rem',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {guest.name}
                  </Typography>
                </Tooltip>

                {/* Guest Details Section */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Email sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      fontWeight: 500,
                      minWidth: 0,
                      overflowWrap: 'anywhere'
                    }}>
                      {guest.email || 'No email'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Phone sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      fontWeight: 500,
                      minWidth: 0,
                      overflowWrap: 'anywhere'
                    }}>
                      {guest.phone || 'No phone'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Home sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      fontWeight: 500,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {guest.address || 'No address'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Category sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      fontWeight: 500,
                      minWidth: 0,
                      overflowWrap: 'anywhere'
                    }}>
                      {guest.category || 'None'}
                    </Typography>
                  </Box>
                </Box>

                {/* Notes Section - Fixed height */}
                <Box sx={{ mb: 2, minHeight: '2.5rem' }}>
                  {guest.notes && (
                    <Tooltip 
                      title={guest.notes} 
                      arrow 
                      placement="top"
                      sx={{ 
                        '& .MuiTooltip-tooltip': {
                          maxWidth: 300,
                          fontSize: '0.875rem',
                          lineHeight: 1.4
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', cursor: 'help' }}>
                        <Notes sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, mt: 0.2, color: theme.palette.primary.main }} />
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          fontSize: { xs: '0.8rem', sm: '0.875rem' }, 
                          fontStyle: 'italic',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {guest.notes}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                </Box>

                {/* RSVP Status - Bottom */}
                <Box sx={{ 
                  mt: 'auto',
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pt: 2,
                  borderTop: `1px solid ${theme.palette.divider}`
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Person sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: getRSVPColor(guest.rsvp) }} />
                  <Typography variant="body2" sx={{ 
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    color: getRSVPColor(guest.rsvp),
                    fontWeight: 600
                  }}>
                    {guest.rsvp}
                  </Typography>
                  </Box>
                  <GuestInvitationButton guest={guest} onClick={() => setInvitationGuest(guest)} />
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MultiViewGuestListManagement;
